'use client';

/**
 * /market — Public Product Catalog (Fase 3 Task D1).
 *
 * Spec ref: SPEC-081 Fase 3 §6.1 (catalog page), §5.1 (public market
 *           routes), §4.1 (listProducts — filter + paginate + sort),
 *           §3.2 (categories constant), SPEC-NEGOCIO §10.4 (INV-MKT-20
 *           admin-approval gate — only `status='approved'` products
 *           reach this page), §10.11 (15 categorías verbatim — DO NOT
 *           INVENT). ADR-039 + enmienda INV-MKT-21 (aprobada por Founder
 *           2026-08-28).
 *
 * PUBLIC (no AppShell — like `/market/tienda/[storeSlug]`). Inherits
 * the AppShell UI chrome from `/market/layout.tsx` (the page file itself
 * does not import AppShell — Fase 2 pattern). The catalog is served to
 * ALL visitors (logged-in or not); the backend `listProducts` independently
 * returns 403 `MARKET_PRODUCTS_DISABLED` when the feature flag is off, so
 * the frontend just renders whatever the API returns.
 *
 * Layout:
 *   - Header: "Profileia Market — Productos"
 *   - Search bar (full-text on title + description — debounced 400ms)
 *   - Filter sidebar (left, sticky on md+):
 *     - Category select (15 entries from `MARKET_CATEGORIES`)
 *     - Subcategory select (dependent on category selection — empty when
 *       no category selected)
 *     - Type filter (physical/digital/service — radio group)
 *     - Price range (min/max inputs in créditos)
 *     - "Limpiar filtros" button
 *   - Sort dropdown (top of grid — newest/price_low/price_high/rating)
 *   - Product grid (responsive 1-2-3-4 cols, uses `<ProductCard />`)
 *   - Pagination (prev/next + page numbers)
 *   - Loading skeletons + error states + empty state
 *
 * §6 Anti-Invención:
 *   - Categories come from `MARKET_CATEGORIES` (imported from
 *     `@profileia/shared`) — NOT hardcoded. Subcategories are derived
 *     from the selected category's `subcategories` array.
 *   - NO `as any` (P-041) — dynamic hrefs cast with `as Route` per
 *     TRAMPA P-067 / CM-51.
 *   - NO `console.*` (P-042).
 *   - AbortController (P-043 / CM-28) — all useEffect fetches pass
 *     `signal` to `api.market.listProducts` and abort on cleanup. The
 *     search input uses a 400ms debounce + a separate AbortController
 *     per keystroke so stale responses are discarded.
 *   - ARIA labels (P-044) — search input, filter selects, sort select,
 *     pagination, and "Limpiar filtros" button all have aria-label or
 *     associated <Label>.
 *
 * Spec ref: SPEC-081 Fase 3 §6.1, §6.5 (ProductCard), §6.7 (api.market.*),
 *           ADR-039, SPEC-NEGOCIO §10.11.
 */

// V124.1-F1 split: useCallback/useEffect/useMemo/useRef/useState + api/
// ApiClientError/setReferralCookie/ProductListResponse/ProductListFilters/
// ProductType/MarketplaceGeoFilter imports removed — moved to
// ./hooks/use-market-page.ts. PAGE_SIZE/SEARCH_DEBOUNCE_MS/SORT_OPTIONS/
// TYPE_OPTIONS/CatalogFilters/DEFAULT_FILTERS también movidos + re-exportados
// (SORT_OPTIONS + TYPE_OPTIONS son los únicos re-usados en el JSX de abajo).
import Link from 'next/link';
import { Search, SlidersHorizontal, Package, AlertCircle, X, Boxes, ArrowRight, Ticket, Gift } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { PageBreadcrumbs } from '@/components/ui/page-breadcrumbs';
import { ProductCard } from '@/components/market/product-card';
import { CreditsBalancePill } from '@/components/market/credits-balance-pill';
import { LocationSelector } from '@/components/market/location-selector';
import { FeaturedBanner } from './components/featured-banner';
// Cart drawer + floating button ahora viven en MarketShell (disponibles en TODAS las páginas /market/*)
// M-8 (Sprint 4): dual-thumb slider for the price range filter — composed
// on top of the existing shadcn Slider primitive (CR-CHECKLIST H2 — no
// parallel mechanism; the wrapper just bridges string<->number state).
import { PriceRangeSlider } from '@/components/market/price-range-slider';
// M-7 (Sprint 4): horizontal rail of recently-viewed products at the top.
import { RecentlyViewedRail } from '@/components/market/recently-viewed-rail';
// SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03 (S1-QW1) — shared slug→label
// helper (DRY fix de raíz per CR-CHECKLIST §H2). Used by the catalog
// category dropdown + the active filter badge below.
import { formatCategoryLabel } from '@/lib/market-labels';
// TASK-FEAT-MARKET-CATEGORIES-DYNAMIC: fetch categories from the API (DB
// with fallback to the constant). The hook uses the constant internally
// for SSR initial state + fail-open fallback.
import { useMarketCategories } from './hooks/use-market-categories';
import { AdNativeInline } from '@/components/ads/AdSlot';
// V124.1-F1: hook import (state + fetch + handlers). SORT_OPTIONS +
// TYPE_OPTIONS son las únicas constantes re-usadas en el JSX de abajo;
// CatalogFilters/DEFAULT_FILTERS/PAGE_SIZE/SEARCH_DEBOUNCE_MS son
// hook-internal only.
import { useMarketPage, SORT_OPTIONS, TYPE_OPTIONS } from './hooks/use-market-page';

// ─── Component ───────────────────────────────────────────────────────────────

export default function MarketCatalogPage() {
  // V124.1-F1 split: state + fetch + handlers extraídos a
  // `./hooks/use-market-page.ts` (MONITOREAR preventivo — el archivo estaba
  // a -42 L del umbral 1000 L). VERBATIM SPEC-244 §4 Estrategia A — los
  // cuerpos de useState/useEffect/useCallback/useMemo son byte-exact al
  // pre-split (lines 200-439). Las 3 llamadas inline `setFilters((prev) =>
  // ...)` del JSX pre-split (geo tab + LocationSelector + Reintentar) se
  // exponen como handlers explícitos (`onGeoTabChange`, `onLocationChange`,
  // `onRetry`) para no filtrar `setFilters` fuera del hook.
  const {
    filters,
    loading,
    loadError,
    data,
    searchInput,
    setSearchInput,
    onSortChange,
    onCategoryChange,
    onSubcategoryChange,
    onTypeChange,
    onMinPriceChange,
    onMaxPriceChange,
    onPriceRangeChange,
    onClearFilters,
    onPageChange,
    onRetry,
    onGeoTabChange,
    onLocationChange,
    subcategories,
    totalPages,
    currentPage,
    hasActiveFilters,
    paginationItems,
  } = useMarketPage();

  // TASK-FEAT-MARKET-CATEGORIES-DYNAMIC: fetch dynamic categories from the
  // API (DB with fallback to the constant). Initial state is the constant
  // for SSR (renders immediately); the API call updates if DB has cats.
  const marketCategories = useMarketCategories();

  // ── Render ──
  return (
    <div className="container mx-auto py-8 px-4">
      <PageBreadcrumbs
        items={[
          { label: 'Inicio', href: '/app' },
          { label: 'Profileia Market', href: '/market/comprar/credits' },
          { label: 'Productos' },
        ]}
      />

      {/* ── Header ── */}
      <header className="mb-6 mt-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Package className="size-7 text-primary" aria-hidden="true" />
            Profileia Market — Productos
          </h1>
          <div className="flex flex-wrap items-center gap-2">
 {/* Track D #20 — prominent link to the bundles
                catalog. No central market nav exists (MarketShell only
                renders logo + Login/Sign Up for anonymous visitors), so
                the products catalog header carries the cross-link to
                /market/combos. Mirrors the Boxes icon used on the combos
                page header for visual consistency. */}
            <Button asChild variant="outline" size="sm">
              <Link
                href="/market/combos"
                aria-label="Ver catálogo de combos (paquetes de productos con descuento)"
              >
                <Boxes className="size-4" aria-hidden="true" />
                Ver combos
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            </Button>
 {/* Track C #15 — cross-link to the cupones catalog
                (Cuponera Cuponatic-style — buyer-facing coupons). Same
                pattern as the bundles cross-link above. */}
            <Button asChild variant="outline" size="sm">
              <Link
                href="/market/cupones"
                aria-label="Ver catálogo de cupones (ofertas de comercios para redimir)"
              >
                <Ticket className="size-4" aria-hidden="true" />
                Ver cupones
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            </Button>
 {/* Track C #15 — cross-link to the gift cards
                catalog (Cuponera — gift cards always-on, always a gift). */}
            <Button asChild variant="outline" size="sm">
              <Link
                href="/market/gift-cards"
                aria-label="Ver catálogo de gift cards (tarjetas de regalo para comercios)"
              >
                <Gift className="size-4" aria-hidden="true" />
                Ver gift cards
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            </Button>
            {/* Sprint 86 (Founder directive 2026-09-11): "en el Marketplace
                se muestre nuestro saldo en creditos para saber cuanto
                tenemos". Hidden for anonymous users — the pill fetches the
                balance only when authenticated. */}
            <CreditsBalancePill />
          </div>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Explora productos físicos, digitales y servicios de vendedores verificados.
          Paga con créditos (1 crédito = 1 USDT).
        </p>
      </header>

 {/* ── Sprint 138 PR-C.2 — Geo Tabs ──
          4 tabs: Local | Nacional | Internacional | Todos.
          Default: '' (all — backward compat). The user can click a tab
          to filter products by the seller's geo location relative to
          the comprador's country/city.

          The selected tab persists in localStorage key
 'profileia:marketplace-filter'. On mount,
          we read the saved value + restore the tab selection.

          userCountryIso + userCityId are populated from:
          1. /api/auth/me response (cuando el user está logueado)
          2. navigator.geolocation + reverse-geocode (cuando no logueado)
          3. Accept-Language header inference (fallback)
          TODO (PR-C.3): wire up the user context fetch. For now, the tabs
          are visible but only 'Todos' returns products — the backend
          will return empty for local/national/international until
 userCountryIso + userCityId are populated. */}
      <div className="mb-6 flex flex-wrap items-center gap-2 border-b pb-2">
      <div
        role="tablist"
        aria-label="Filtro geográfico del Marketplace"
        className="flex flex-wrap gap-2"
      >
        {(['local', 'national', 'international', 'all'] as const).map((tab) => {
          const isActive = filters.geo === tab;
          const label =
            tab === 'local' ? '🟢 Local' :
            tab === 'national' ? '🟡 Nacional' :
            tab === 'international' ? '🔵 Internacional' :
            'Todos';
          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onGeoTabChange(tab)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
      {/* S3-H7 — LocationSelector seeds filters.userCountryIso + userCityId
          from localStorage / /api/auth/me + lets the buyer override them. */}
      <LocationSelector currentCountryIso={filters.userCountryIso} currentCityId={filters.userCityId} onLocationChange={onLocationChange} />
      </div>

      {/* ── Search bar (full-width) ── */}
      <div className="mb-6">
        <Label htmlFor="market-search" className="sr-only">
          Buscar productos
        </Label>
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            id="market-search"
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar por título, descripción o ubicación..."
            className="pl-10"
            aria-label="Buscar productos por título o descripción"
            autoComplete="off"
          />
          {searchInput && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 px-2"
              onClick={() => setSearchInput('')}
              aria-label="Limpiar búsqueda"
            >
              <X className="size-4" aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>

      {/* M-7 (Sprint 4): recently-viewed rail — reads localStorage; renders
          nothing if empty (no empty-state UI per the spec). */}
      <RecentlyViewedRail />

      {/* ── Main grid: sidebar + products ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* ── Filter sidebar ── */}
        <aside className="lg:sticky lg:top-4 lg:self-start" aria-label="Filtros del catálogo">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <SlidersHorizontal className="size-4" aria-hidden="true" />
                Filtros
              </CardTitle>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 h-7 w-fit text-xs"
                  onClick={onClearFilters}
                  aria-label="Limpiar todos los filtros"
                >
                  Limpiar filtros
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Category */}
              <div className="space-y-1.5">
                <Label htmlFor="filter-category" className="text-xs font-medium">
                  Categoría
                </Label>
                <Select
                  value={filters.category || 'all'}
                  onValueChange={onCategoryChange}
                >
                  <SelectTrigger id="filter-category" className="w-full" aria-label="Filtrar por categoría">
                    <SelectValue placeholder="Todas las categorías" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las categorías</SelectItem>
                    {marketCategories.map((cat) => (
                      <SelectItem key={cat.category} value={cat.category}>
                        {formatCategoryLabel(cat.category)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subcategory (dependent on category) */}
              <div className="space-y-1.5">
                <Label htmlFor="filter-subcategory" className="text-xs font-medium">
                  Subcategoría
                </Label>
                <Select
                  value={filters.subcategory || 'all'}
                  onValueChange={onSubcategoryChange}
                  disabled={!filters.category}
                >
                  <SelectTrigger
                    id="filter-subcategory"
                    className="w-full"
                    aria-label={
                      filters.category
                        ? 'Filtrar por subcategoría'
                        : 'Selecciona una categoría primero'
                    }
                  >
                    <SelectValue placeholder={
                      filters.category ? 'Todas las subcategorías' : 'Selecciona una categoría'
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las subcategorías</SelectItem>
                    {subcategories.map((sub) => (
                      <SelectItem key={sub} value={sub}>
                        {sub}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Type */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Tipo de producto</Label>
                <RadioGroup
                  value={filters.type}
                  onValueChange={onTypeChange}
                  className="grid grid-cols-2 gap-1.5 pt-1"
                  aria-label="Filtrar por tipo de producto"
                >
                  {TYPE_OPTIONS.map((opt) => (
                    <div key={opt.value || 'all'} className="flex items-center gap-1.5">
                      <RadioGroupItem id={`type-${opt.value || 'all'}`} value={opt.value} />
                      <Label htmlFor={`type-${opt.value || 'all'}`} className="text-xs cursor-pointer">
                        {opt.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Price range */}
              <div className="space-y-2">
                <Label className="text-xs font-medium">Rango de precio (créditos)</Label>
                {/* M-8 (Sprint 4): dual-thumb slider (0-1000 USDT, step 10).
                    Composes the existing shadcn Slider primitive. The number
                    inputs below remain as fallback for precise entry + values
                    outside the slider's 0-1000 range (per spec M-8). */}
                <PriceRangeSlider
                  minPrice={filters.minPrice}
                  maxPrice={filters.maxPrice}
                  onChange={onPriceRangeChange}
                />
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={filters.minPrice}
                    onChange={onMinPriceChange}
                    placeholder="Mín"
                    className="h-8 text-xs"
                    aria-label="Precio mínimo en créditos"
                  />
                  <span className="text-xs text-muted-foreground" aria-hidden="true">—</span>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={filters.maxPrice}
                    onChange={onMaxPriceChange}
                    placeholder="Máx"
                    className="h-8 text-xs"
                    aria-label="Precio máximo en créditos"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </aside>

        {/* ── Featured banner (paid placement — hidden when no placements) ── */}
        <FeaturedBanner limit={5} />

        {/* ── Products section ── */}
        <section aria-label="Lista de productos">
          {/* Sort + count bar */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {loading ? (
                'Cargando productos...'
              ) : data ? (
                <>
                  {data.total} {data.total === 1 ? 'producto' : 'productos'}
                  {hasActiveFilters ? ' encontrados' : ' en total'}
                </>
              ) : (
                '—'
              )}
            </p>
            <div className="flex items-center gap-2">
              <Label htmlFor="filter-sort" className="text-xs text-muted-foreground">
                Ordenar por:
              </Label>
              <Select value={filters.sortBy} onValueChange={onSortChange}>
                <SelectTrigger id="filter-sort" className="h-8 w-[200px] text-xs" aria-label="Ordenar productos">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ── Loading skeletons ── */}
          {loading && (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
              aria-label="Cargando productos"
              aria-busy="true"
            >
              {[...Array(8)].map((_, i) => (
                <div key={i} className="rounded-lg border border-border bg-card overflow-hidden">
                  <Skeleton className="aspect-[4/3] w-full" />
                  <div className="p-3 space-y-2">
                    <Skeleton className="h-3 w-16" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-2/3" />
                    <Skeleton className="h-5 w-20" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Error state ── */}
          {!loading && loadError && (
            <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base text-amber-700 dark:text-amber-400">
                  <AlertCircle className="size-5" aria-hidden="true" />
                  No se pudo cargar el catálogo
                </CardTitle>
                <CardDescription className="text-amber-700 dark:text-amber-400">
                  {loadError}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  onClick={onRetry}
                  aria-label="Reintentar carga del catálogo"
                >
                  Reintentar
                </Button>
              </CardContent>
            </Card>
          )}

          {/* ── Empty state ── */}
          {!loading && !loadError && data && data.products.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10">
                  <Package className="size-6 text-primary" aria-hidden="true" />
                </div>
                <p className="font-medium text-sm">No se encontraron productos</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                  {hasActiveFilters
                    ? 'Prueba ajustando o limpiando los filtros para ver más resultados.'
                    : 'Los vendedores aún no han publicado productos. Vuelve pronto.'}
                </p>
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={onClearFilters}
                    aria-label="Limpiar filtros para ver todos los productos"
                  >
                    Limpiar filtros
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* ── Product grid ── */}
          {!loading && !loadError && data && data.products.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {data.products.map((product, index) => (
                <>
                  <ProductCard key={product.productId} product={product} />
                  {/* AdSense — Native ad cada 6 productos (tier free+basic) */}
                  {(index + 1) % 6 === 0 && <AdNativeInline />}
                </>
              ))}
            </div>
          )}

          {/* ── Pagination ── */}
          {!loading && !loadError && data && data.products.length > 0 && totalPages > 1 && (
            <Pagination className="mt-8">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage > 1) onPageChange(currentPage - 1);
                    }}
                    aria-disabled={currentPage === 1}
                    aria-label={`Página anterior (actual: ${currentPage})`}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                {paginationItems.map((item) => (
                  <PaginationItem key={item.page}>
                    <PaginationLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onPageChange(item.page);
                      }}
                      isActive={item.page === currentPage}
                      aria-label={`Página ${item.page}`}
                      aria-current={item.page === currentPage ? 'page' : undefined}
                    >
                      {item.label}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage < totalPages) onPageChange(currentPage + 1);
                    }}
                    aria-disabled={currentPage === totalPages}
                    aria-label={`Página siguiente (actual: ${currentPage})`}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}

          {/* Active filter chips (visible state for screen readers + sighted users) */}
          {!loading && hasActiveFilters && (
            <div className="mt-4 flex flex-wrap items-center gap-2" aria-label="Filtros activos">
              <span className="text-xs text-muted-foreground">Filtros activos:</span>
              {filters.search && (
                <Badge variant="secondary" className="text-xs">«{filters.search}»</Badge>
              )}
              {filters.category && (
                <Badge variant="secondary" className="text-xs">
                  {formatCategoryLabel(filters.category)}
                </Badge>
              )}
              {filters.subcategory && (
                <Badge variant="secondary" className="text-xs">{filters.subcategory}</Badge>
              )}
              {filters.type && (
                <Badge variant="secondary" className="text-xs">
                  {TYPE_OPTIONS.find((t) => t.value === filters.type)?.label}
                </Badge>
              )}
              {filters.minPrice && (
                <Badge variant="secondary" className="text-xs">≥ {filters.minPrice} créditos</Badge>
              )}
              {filters.maxPrice && (
                <Badge variant="secondary" className="text-xs">≤ {filters.maxPrice} créditos</Badge>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
